import { localStorage } from '@utils/storage';
import { STORAGE_KEYS } from '@constants';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api';

class CareerMapService {
  
  // Get auth token from localStorage
  getAuthToken() {
    const token = localStorage.get(STORAGE_KEYS.ACCESS_TOKEN);
    // console.log('🔐 CareerMapService - Token retrieved:', token ? 'EXISTS' : 'NULL', 'Key:', STORAGE_KEYS.ACCESS_TOKEN);
    return token;
  }

  // Get auth headers
  getAuthHeaders() {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  // Get all categories with subcategories
  async getCategories() {
    const response = await fetch(`${API_BASE}/career-map/categories`);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch categories');
    }
    
    return data.data;
  }

  // Get subcategory with all stages
  async getSubcategory(subcategoryId) {
    const response = await fetch(`${API_BASE}/career-map/subcategories/${subcategoryId}`);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch subcategory');
    }
    
    return data.data;
  }

  // Get user's all progress
  async getUserProgress() {
    const response = await fetch(`${API_BASE}/career-map/user/progress`, {
      headers: this.getAuthHeaders()
    });
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch user progress');
    }
    
    return data.data;
  }

  // Get user's progress for specific subcategory
  async getUserProgressBySubcategory(subcategoryId) {
    const response = await fetch(`${API_BASE}/career-map/user/progress/${subcategoryId}`, {
      headers: this.getAuthHeaders()
    });
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch progress');
    }
    
    return data.data;
  }

  // Save or update stage progress
  async saveProgress(subcategoryId, stageId, status, fieldData) {
    // console.log('🚀 CareerMapService.saveProgress called');
    // console.log('📤 POST URL:', `${API_BASE}/career-map/user/progress`);
    // console.log('📤 POST Headers:', this.getAuthHeaders());
    // console.log('📤 POST Body:', { subcategoryId, stageId, status, fieldData });
    
    const response = await fetch(`${API_BASE}/career-map/user/progress`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        subcategoryId,
        stageId,
        status,
        fieldData
      })
    });
    
    console.log('📥 Response status:', response.status);
    console.log('📥 Response ok:', response.ok);
    
    const data = await response.json();
    console.log('📥 Response data:', data);
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to save progress');
    }
    
    return data.data;
  }

  // Reset subcategory progress
  async resetProgress(subcategoryId) {
    const response = await fetch(`${API_BASE}/career-map/user/progress/${subcategoryId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to reset progress');
    }
    
    return data.data;
  }

  // Get activity logs
  async getActivityLogs(subcategoryId = null, limit = 50, offset = 0) {
    const params = new URLSearchParams({ limit, offset });
    if (subcategoryId) {
      params.append('subcategoryId', subcategoryId);
    }
    
    const response = await fetch(`${API_BASE}/career-map/user/activity-logs?${params}`, {
      headers: this.getAuthHeaders()
    });
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch activity logs');
    }
    
    return data.data;
  }

  // Helper: Convert backend progress to frontend state format
  convertProgressToState(progressArray, subcategoriesMap) {
    const stageData = {};
    
    progressArray.forEach(progress => {
      // Find subcategory slug
      const subcategory = subcategoriesMap[progress.subcategoryId];
      if (!subcategory) return;
      
      // Create key: subcategorySlug_stageNumber
      const key = `${subcategory.slug}_${progress.stage.stageNumber}`;
      
      // Merge status and field data
      stageData[key] = {
        status: progress.status,
        ...progress.fieldData
      };
    });
    
    return stageData;
  }

  // Helper: Find subcategory ID by slug
  findSubcategoryId(categories, slug) {
    console.log('🔎 findSubcategoryId - Looking for slug:', slug);
    console.log('🔎 findSubcategoryId - Categories structure:', categories);
    
    for (const category of categories) {
      // Handle both backend (subcategories) and local JSON (subCategories) structure
      const subs = category.subcategories || category.subCategories;
      console.log('🔎 Category:', category.name, 'Subcategories:', subs);
      
      const subcategory = subs?.find(sub => sub.slug === slug || sub.id === slug);
      if (subcategory) {
        console.log('✅ Found subcategory:', subcategory);
        return subcategory.id;
      }
    }
    console.log('❌ Subcategory not found for slug:', slug);
    return null;
  }

  // Helper: Find stage ID by subcategory ID and stage number
  findStageId(subcategory, stageNumber) {
    const stage = subcategory.stages?.find(s => s.stageNumber === stageNumber);
    return stage?.id || null;
  }
}

export default new CareerMapService();
